// @ts-nocheck
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { BreakpointKey } from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { blogTileImageStyles, blogTileReadMoreStyles, blogTileWrapperStyles } from "@store/Themes/common/mixins/blog";
import { transitionAnimationStyles } from "@store/Themes/common/mixins/transition";
import { blogProps } from "@store/Themes/common/props/blog";
import useCurrentBreakpointValue from "@toolkit/Framework/Common/Hooks/useCurrentBreakpointValue";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import stripHtml from "@toolkit/Framework/Common/Utilities/stripHtml";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import parse from "html-react-parser";
import truncate from "lodash/truncate";
import React, { BaseHTMLAttributes, FC, HTMLAttributes, useMemo } from "react";
import { css } from "styled-components";

const mobileBreakpoint: BreakpointKey = "md";
const tileContentPaddingBottom = 24;

export interface BlogFeaturedCategoryTilePresentationProps extends ExpandStyles<BlogFeaturedCategoryTileStyles> {}

export interface BlogFeaturedCategoryTileProps extends BlogFeaturedCategoryTilePresentationProps {
    imagePath: string;
    linkHref: string | undefined;
    readMoreLabel: string;
    subheading?: string;
    title: string;
}

export interface BlogFeaturedCategoryTileStyles {
    tileClickable?: ClickablePresentationProps;
    overlay?: InjectableCss;
    imageWrapper?: InjectableCss;
    image?: LazyImageProps;
    content?: InjectableCss;
    title?: TypographyPresentationProps;
    subheading?: TypographyPresentationProps;
    readMore?: TypographyPresentationProps;
}

export const blogFeaturedCategoryTileStyles: BlogFeaturedCategoryTileStyles = {
    tileClickable: {
        css: css`
            ${blogTileWrapperStyles()}
            overflow: hidden;
            color: ${getColor("invert")};

            &:hover {
                .readMore {
                    text-decoration: underline;
                }

                ${breakpoint(mobileBreakpoint)} {
                    .content {
                        bottom: 0;
                    }
                }
            }
        `,
    },
    overlay: {
        css: css`
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            background: linear-gradient(
                180deg,
                rgba(102, 102, 102, 0) -999%,
                rgba(102, 102, 102, 0) -20%,
                rgba(0, 0, 0, 0.78) 100%
            );
        `,
    },
    imageWrapper: mergeProps<BlogFeaturedCategoryTileStyles["imageWrapper"]>(blogProps.imageWrapper, {
        css: css`
            width: 100%;
        `,
    }),
    image: {
        css: css`
            ${x => x.theme.lazyImage.defaultProps?.css}
            display: flex;

            img {
                ${blogTileImageStyles()}
            }
        `,
    },
    content: {
        css: css`
            transition: ${transitionAnimationStyles("bottom")};
            position: absolute;
            bottom: 0;
            width: 100%;
            padding: ${pxToRem(16)};

            ${breakpoint(mobileBreakpoint)} {
                bottom: -${pxToRem(50)};
                padding: ${pxToRem(24, 64, tileContentPaddingBottom, 24)};
            }
        `,
    },
    title: {
        variant: "h4",
        forwardAs: "h3",
        css: css`
            line-height: ${x => x.theme.design.typography.lineHeight.base};
        `,
    },
    readMore: {
        css: css`
            ${blogTileReadMoreStyles("invert")}
            ${spacingStyles(pxToRem(tileContentPaddingBottom))}
        `,
    },
};

const BlogFeaturedCategoryTile: FC<BlogFeaturedCategoryTileProps> = ({
    extendedStyles,
    overridedStyles,
    subheading: baseSubheading,
    imagePath,
    linkHref,
    readMoreLabel,
    title: baseTitle,
    ...htmlAttributes
}) => {
    const styles = useExpandProps(blogFeaturedCategoryTileStyles, extendedStyles, overridedStyles);
    const titleTruncateValue = useCurrentBreakpointValue([150, 90, 120, 200, 200]);
    const subheadingTruncateValue = useCurrentBreakpointValue([0, 0, 150, 335, 660]);

    const title = useMemo(() => {
        return truncate(baseTitle, { length: titleTruncateValue });
    }, [titleTruncateValue]);

    const subheading = useMemo(() => {
        return truncate(baseSubheading, { length: subheadingTruncateValue });
    }, [subheadingTruncateValue]);

    const subheadingStripHtml = useMemo(() => {
        return subheadingTruncateValue && subheadingTruncateValue > 0 ? stripHtml(subheading || "") : "";
    }, [subheadingTruncateValue]);

    if (!linkHref) {
        return null;
    }

    return (
        <Clickable {...styles.tileClickable} href={linkHref} {...htmlAttributes}>
            <StyledWrapper {...styles.imageWrapper} aria-hidden="true">
                <LazyImage {...styles.image} src={imagePath} />
            </StyledWrapper>
            <StyledWrapper {...styles.overlay} aria-hidden="true" />
            <StyledWrapper {...styles.content} className="content">
                <Typography {...styles.title} aria-label={`${title} ${subheadingStripHtml}`}>
                    {title}
                </Typography>
                {subheading && subheadingTruncateValue && subheadingTruncateValue > 0 ? (
                    <StyledWrapper {...styles.subheading} aria-hidden="true">
                        {parse(subheading, parserOptions)}
                    </StyledWrapper>
                ) : null}
                <Typography {...styles.readMore} className="readMore" aria-hidden="true">
                    {readMoreLabel}
                </Typography>
            </StyledWrapper>
        </Clickable>
    );
};

export default BlogFeaturedCategoryTile;
